import React, {useEffect, useRef, useState} from "react";
import {Button, Column, Row, Spinner, Text} from "react-vcomponents";
import {observer_simple} from "web-vcore";
import {observer_mgl, RunInAction} from "mobx-graphlink";
import {ShowMessageBox} from "react-vmessagebox";
import {nativeBridge} from "../Bridge/Bridge_Native.js";
import {Remote_RequestAudioPlayPause, Remote_RequestAudioSpeed} from "../Bridge/Bridge_RemoteUserProcesses.js";
import {store} from "../../Store/index.js";

export type AudioBlockInfo = {
	fileName: string;
	blockStartTime: number; // epoch ms
	absolutePath: string;
};

type AudioBlockPlayerProps = {
	/** Epoch-ms timestamp to seek to; null/undefined means no timestamp near cursor (player hidden). */
	wallClockTime: number|n;
};

export const AudioBlockPlayer = observer_mgl((props: AudioBlockPlayerProps)=>{
	const {wallClockTime} = props;
	const uiState = store.main.settings.transcribe;
	const remotePlayPause = uiState.remotePlayPause;
	const playbackRate = uiState.audioPlaybackRate;
	const [audioBlockInfos, setAudioBlockInfos] = useState<AudioBlockInfo[]|null>(null);
	const [speedDropdownOpen, setSpeedDropdownOpen] = useState(false);
	const audioRef = useRef<HTMLAudioElement>(null);
	/** When true, cursor movement auto-plays the target audio block. (in addition to auto-seeking)
	Set true when user clicks "play"; set false when user clicks "pause". */
	const autoPlayOnSeekRef = useRef(false);

	// load block list once on mount
	useEffect(()=>{
		(nativeBridge.Call("WhisperGetAudioBlockInfos") as Promise<AudioBlockInfo[]>)
			.then(infos=>setAudioBlockInfos(infos ?? []))
			.catch(()=>setAudioBlockInfos([]));
	}, []);

	// find the last block that started at or before the timestamp (blocks are sorted ascending by the native side)
	const sortedBlocks = audioBlockInfos ? [...audioBlockInfos].OrderBy(a=>a.blockStartTime) : [];
	const matchingBlock = wallClockTime != null && sortedBlocks.length > 0
		? (sortedBlocks.filter(b=>b.blockStartTime <= wallClockTime).at(-1) ?? null)
		: null;

	// seek (and optionally auto-play) whenever cursor-derived timestamp or block changes
	useEffect(()=>{
		if (!audioRef.current || !matchingBlock || wallClockTime == null) return;
		const seekSeconds = (wallClockTime - matchingBlock.blockStartTime) / 1000;
		audioRef.current.currentTime = seekSeconds;
		if (autoPlayOnSeekRef.current) audioRef.current.play();
	}, [matchingBlock, matchingBlock?.fileName, wallClockTime]);

	// handle explicit remote play/pause requests (fires once per signal id, not on rerenders or remounts)
	/** Tracks the last remotePlayPause id we've acted on; initialized to current id so stale signals on mount are ignored. */
	const lastHandledPlayPauseId = useRef(remotePlayPause?.id ?? -1);
	useEffect(()=>{
		if (!remotePlayPause || !audioRef.current) return;
		if (remotePlayPause.id <= lastHandledPlayPauseId.current) return;
		lastHandledPlayPauseId.current = remotePlayPause.id;
		if (remotePlayPause.play) audioRef.current.play();
		else audioRef.current.pause();
	}, [remotePlayPause, remotePlayPause?.id]);


	useEffect(()=>{ if (audioRef.current) audioRef.current.playbackRate = playbackRate; }, [playbackRate]);

	if (audioBlockInfos == null) return null; // still loading
	if (!matchingBlock) {
		if (wallClockTime == null) return null; // no timestamp near cursor — hide entirely
		return <Row mt={3}><Text style={{opacity: .5, fontSize: 12}}>No audio block found for this timestamp.</Text></Row>;
	}

	// Capacitor converts file:// paths to http://localhost/_capacitor_file_/... for WebView access
	const audioSrc = (window as any).Capacitor?.convertFileSrc("file://" + matchingBlock.absolutePath) ?? ("file://" + matchingBlock.absolutePath);

	const seek = (deltaSeconds: number)=>{
		if (!audioRef.current) return;
		audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime + deltaSeconds);
	};

	return (
		<Row mt={3} center style={{flexWrap: "wrap", gap: 4}}>
			{/*<Text style={{fontSize: 12, opacity: .7}}>Audio:</Text>*/}
			<audio ref={audioRef} src={audioSrc} controls style={{height: 28, flex: 1, minWidth: 180}}
				onPlay={()=>{
					autoPlayOnSeekRef.current = true;
				}}
				onPause={()=>{
					// only disable auto-play-on-seek if pause was from explicit user action (ie. not by reaching end-of-block)
					if (!audioRef.current?.ended) {
						autoPlayOnSeekRef.current = false;
					}
				}}/>
			<div style={{position: "relative"}}>
				<Button p="2px 6px" style={{fontSize: 11}} text={`${playbackRate}x`} onClick={()=>setSpeedDropdownOpen(o=>!o)}/>
				{speedDropdownOpen &&
				<div style={{
					position: "absolute", bottom: "100%", left: 0, zIndex: 100,  padding: 4,
					display: "flex", flexDirection: "column", gap: 2,
					background: "#222", border: "1px solid #555", borderRadius: 4,
				}}>
					{PRESET_SPEEDS.map(s=>(
						<Button key={s} p="2px 8px" style={{fontSize: 12, fontWeight: playbackRate == s ? "bold" : undefined, whiteSpace: "nowrap"}}
							text={`${s}x`} onClick={()=>{
								RunInAction("setAudioPlaybackRate", ()=>store.main.settings.transcribe.audioPlaybackRate = s);
								setSpeedDropdownOpen(false);
							}}/>
					))}
					<Button p="2px 8px" style={{fontSize: 12}} text="*" onClick={()=>{
						setSpeedDropdownOpen(false);
						ShowCustomSpeedDialog(playbackRate, rate=>RunInAction("setAudioPlaybackRate", ()=>store.main.settings.transcribe.audioPlaybackRate = rate));
					}}/>
				</div>}
			</div>
			<Button p="2px 6px" style={{fontSize: 11}} text="-5s" onClick={()=>seek(-5)}/>
			<Button p="2px 6px" style={{fontSize: 11}} text="+5s" onClick={()=>seek(5)}/>
		</Row>
	);
});

/** Shown on the cursor-sending side; lets the local user control play/pause and speed on the remote viewer's AudioBlockPlayer. */
export const AudioBlockPlayer_Proxy = observer_mgl(()=>{
	const [playing, setPlaying] = useState(false);
	const playbackRate = store.main.settings.transcribe.audioPlaybackRate;
	const applyRate = (rate: number)=>{
		// set the global audio-block playback-rate for the local LF-instance as well as the remote one
		RunInAction("setAudioPlaybackRate", ()=>store.main.settings.transcribe.audioPlaybackRate = rate);
		Remote_RequestAudioSpeed(rate);
	};
	return (
		<Row mt={3} center style={{gap: 6, flexWrap: "wrap"}}>
			<Text style={{fontSize: 12, opacity: .7}}>Remote audio:</Text>
			<Button p="2px 8px" style={{fontSize: 11}} text={playing ? "⏸ Pause" : "▶ Play"} onClick={()=>{
				if (playing) {
					Remote_RequestAudioPlayPause(false);
					setPlaying(false);
				} else {
					Remote_RequestAudioPlayPause(true);
					setPlaying(true);
				}
			}}/>
			{PRESET_SPEEDS_EXTENDED.map(s=>(
				<Button key={s} p="2px 6px" style={{fontSize: 11, fontWeight: playbackRate == s ? "bold" : undefined}}
					text={`${s}x`} onClick={()=>applyRate(s)}/>
			))}
			<Button p="2px 6px" style={{fontSize: 11}} text="*" onClick={()=>ShowCustomSpeedDialog(playbackRate, applyRate)}/>
		</Row>
	);
});

const PRESET_SPEEDS = [0.5, 1, 1.25, 1.5, 1.75, 2];
const PRESET_SPEEDS_EXTENDED = [...PRESET_SPEEDS, 2.5, 3, 3.5, 4];
function ShowCustomSpeedDialog(currentRate: number, onSelect: (rate: number)=>void) {
	let customRate = currentRate;
	const ctrl = ShowMessageBox({
		title: "Custom playback speed", okButton: true, cancelButton: true,
		message: ()=>{
			ctrl.UpdateOptions({okButtonProps: {enabled: customRate > 0}});
			return <Row center style={{gap: 8}}>
				<Text>Speed:</Text>
				<Spinner value={customRate} onChange={val=>{ customRate = val; ctrl.UpdateUI(); }} step={0.05} min={0.1} max={4} style={{width: 70}}/>
				<Text>x</Text>
			</Row>;
		},
		onOK: ()=>onSelect(customRate),
	});
}